import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  PLATFORM_ID,
} from '@angular/core';
import type { EChartsCoreOption } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { SeriesPoint } from '../../../core/models/domain.models';
import { ThemeService } from '../../../core/services/theme.service';

/** Tiny inline sparkline (12-month pulse). */
@Component({
  selector: 'app-sparkline',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
  templateUrl: './sparkline.component.html',
  styleUrl: './sparkline.component.scss',
})
export class SparklineComponent {
  private readonly theme = inject(ThemeService);
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  series = input.required<SeriesPoint[]>();

  readonly hasValidSeries = computed(() => {
    const s = this.series();
    if (!s || s.length < 2) return false;
    const finiteCount = s.filter((p) => p.value != null && isFinite(p.value)).length;
    return finiteCount >= 2;
  });

  options = computed<EChartsCoreOption>(() => {
    const dark = this.theme.theme() === 'dark';
    const color = dark ? '#3FB8AE' : '#0F8B84';
    return {
      grid: { left: 0, right: 0, top: 4, bottom: 0 },
      xAxis: { type: 'category', show: false, data: this.series().map((p) => p.period) },
      yAxis: { type: 'value', show: false, scale: true },
      tooltip: { show: false },
      series: [
        {
          type: 'line',
          data: this.series().map((p) => p.value),
          smooth: 0.3,
          symbol: 'none',
          lineStyle: { width: 1.6, color },
          areaStyle: { opacity: 0.12, color },
        },
      ],
      animation: false,
    };
  });
}
